/**
 * Greenlight Page — Tab within ProjectWorkspace.
 * Handles: upload trigger, processing state, results display, evolution tracking.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, RefreshCw, History, Zap } from 'lucide-react';
import GreenlightDashboardV2 from '@/components/greenlight/GreenlightDashboardV2';
import GreenlightProcessing from '@/components/greenlight/GreenlightProcessing';
import ScriptEvolution from '@/components/greenlight/ScriptEvolution';
import {
  runGreenlightAnalysis,
  getGreenlightStatus,
  getProjectGreenlight,
  getGreenlightHistory,
  getByokAnalysisStatus,
  fillMissingAnalysis,
} from '@/services/greenlightService';

export default function GreenlightPage({ projectId, script: scriptProp, project }) {
  const script = scriptProp || {};
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisId, setAnalysisId] = useState(null);
  const [isByok, setIsByok] = useState(false);
  const [showEvolution, setShowEvolution] = useState(false);
  const pollRef = useRef(null);

  // Load existing analysis + history
  useEffect(() => {
    loadExistingAnalysis();
    loadHistory();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [projectId]);

  const loadExistingAnalysis = async () => {
    setLoading(true);
    try {
      const data = await getProjectGreenlight(projectId);
      if (data.exists && data.status === 'completed') {
        setAnalysis(data);
      } else if (data.exists && data.status === 'processing') {
        setAnalysis(data);
        setAnalyzing(true);
        setAnalysisId(data.id);
        startPolling(data.id, false);
      }
    } catch (err) {
      // No analysis yet — that's fine
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const h = await getGreenlightHistory(projectId);
      setHistory(h || []);
    } catch {
      // History not available
    }
  };

  const startPolling = useCallback((id, byok) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        let status;
        if (byok) {
          status = getByokAnalysisStatus(id);
        } else {
          status = await getGreenlightStatus(id);
        }

        if (!status) return;

        setAnalysis(status);

        if (status.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAnalyzing(false);
          loadHistory(); // Refresh history after completion
          toast.success(`Greenlight Score: ${Math.round(status.greenlight_score)}/100`);
        } else if (status.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAnalyzing(false);
          toast.error(`Analysis failed: ${status.error || 'Unknown error'}`);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, byok ? 1000 : 2000);
  }, []);

  // Detect missing analysis sections
  const ANALYSIS_FIELDS = [
    'writing_craft', 'characters', 'emotional_architecture', 'narrative_logic',
    'commercial', 'character_arcs', 'budget', 'risk', 'dead_zones',
    'audience_segmentation', 'comparable_films', 'act_summaries', 'cross_module_insights',
  ];

  const missingCount = analysis && analysis.status === 'completed'
    ? ANALYSIS_FIELDS.filter(f => {
        const v = analysis[f];
        return !v || (typeof v === 'object' && Object.keys(v).length === 0);
      }).length
    : 0;

  const handleFillMissing = async () => {
    if (!analysis?.id) return;
    setAnalyzing(true);
    try {
      const result = await fillMissingAnalysis(analysis.id);
      setAnalysisId(result.analysis_id);
      setIsByok(false);
      setAnalysis(prev => ({ ...prev, status: 'processing', progress: 0, current_step: 'Checking for missing data...' }));
      startPolling(result.analysis_id, false);
    } catch (err) {
      console.error('Fill missing failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to fill missing data');
      setAnalyzing(false);
    }
  };

  const handleRunAnalysis = async () => {
    if (!script?.content || script.content.length < 200) {
      toast.error('Upload a script first (minimum 200 characters)');
      return;
    }

    setAnalyzing(true);
    try {
      const result = await runGreenlightAnalysis(
        projectId,
        script?.content,
        project?.title || script?.title || 'Untitled'
      );

      const id = result.analysis_id;
      const byok = id.startsWith('byok-');

      setAnalysisId(id);
      setIsByok(byok);
      setAnalysis({ status: 'processing', progress: 0, current_step: 'Starting...' });
      startPolling(id, byok);
    } catch (err) {
      console.error('Analysis launch failed:', err);
      toast.error('Failed to start analysis');
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--text-muted)]" />
      </div>
    );
  }

  // No analysis yet — show CTA (Screening Room aesthetic)
  if (!analysis && !analyzing) {
    return (
      <div className="h-full overflow-y-auto" style={{ background: 'var(--sr-deep, #09090B)' }}>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Hero icon */}
            <div
              className="w-20 h-20 mx-auto mb-8 rounded-2xl flex items-center justify-center"
              style={{
                background: 'var(--sr-gold-glow, rgba(201,168,76,0.15))',
                border: '1px solid rgba(201,168,76,0.2)',
              }}
            >
              <Sparkles size={32} style={{ color: 'var(--sr-gold, #C9A84C)' }} />
            </div>

            <h2
              className="text-3xl mb-3"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                color: 'var(--sr-text-primary, #F4F4F5)',
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
              }}
            >
              Greenlight Engine
            </h2>
            <p
              className="max-w-md mx-auto mb-10"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 15,
                color: 'var(--sr-text-tertiary, #71717A)',
                lineHeight: 1.6,
              }}
            >
              AI-powered script intelligence. Get a comprehensive analysis of your screenplay's
              writing craft, characters, emotional architecture, logic, and commercial viability.
            </p>

            <button
              onClick={handleRunAnalysis}
              disabled={!script?.content}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 32px',
                borderRadius: 12,
                background: !script?.content ? 'rgba(201,168,76,0.15)' : 'var(--sr-gold, #C9A84C)',
                border: 'none',
                cursor: !script?.content ? 'not-allowed' : 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 15,
                fontWeight: 600,
                color: !script?.content ? 'var(--sr-text-tertiary, #71717A)' : 'var(--sr-void, #000)',
                letterSpacing: '0.01em',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                if (script?.content) {
                  e.currentTarget.style.background = 'var(--sr-gold-bright, #D4B85A)';
                  e.currentTarget.style.boxShadow = '0 0 24px rgba(201,168,76,0.3)';
                }
              }}
              onMouseLeave={e => {
                if (script?.content) {
                  e.currentTarget.style.background = 'var(--sr-gold, #C9A84C)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <Sparkles size={16} />
              Run Greenlight Analysis
            </button>

            {!script?.content && (
              <p
                className="mt-3"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 11,
                  color: 'var(--sr-text-tertiary, #71717A)',
                }}
              >
                Upload a script in the Script tab first
              </p>
            )}

            {/* Feature Previews */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-14 text-left">
              {[
                { label: 'Greenlight Score', desc: '0-100 composite with badge tier' },
                { label: 'Writing Craft', desc: '10-category radar analysis' },
                { label: 'Characters & Arcs', desc: 'Depth profiling + arc tracking' },
                { label: 'Emotions', desc: '12-emotion timeline heatmap' },
                { label: 'Logic Check', desc: 'Plot holes & thread tracking' },
                { label: 'Budget & Risk', desc: 'Per-scene cost & risk estimation' },
                { label: 'Audience', desc: 'Parliament segmentation charts' },
                { label: 'Pitch Deck', desc: 'One-click exportable pitch deck' },
              ].map((f) => (
                <div
                  key={f.label}
                  className="p-3 rounded-lg"
                  style={{
                    background: 'var(--sr-surface, #111114)',
                    border: '1px solid var(--sr-border-subtle, #222228)',
                  }}
                >
                  <p
                    className="text-xs font-medium"
                    style={{ color: 'var(--sr-text-primary, #F4F4F5)', fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {f.label}
                  </p>
                  <p
                    className="mt-0.5"
                    style={{ fontSize: 10, color: 'var(--sr-text-tertiary, #71717A)', fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Processing
  if (analyzing && analysis?.status === 'processing') {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-10">
          <GreenlightProcessing
            progress={analysis.progress || 0}
            currentStep={analysis.current_step || 'Processing...'}
            analysis={analysis}
          />
        </div>
      </div>
    );
  }

  // Results — V2 card grid layout
  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--sr-deep, #09090B)' }}>
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 lg:px-8">
          <div className="flex items-center gap-3">
            <Sparkles size={16} style={{ color: 'var(--sr-gold, #C9A84C)' }} />
            <h2
              className="text-sm font-semibold"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: 'var(--sr-text-primary, #F4F4F5)',
                fontSize: 16,
              }}
            >
              Greenlight Report
            </h2>
            {analysis?.title && (
              <span
                className="text-xs"
                style={{ color: 'var(--sr-text-tertiary, #71717A)', fontFamily: "'DM Sans', sans-serif" }}
              >
                — {analysis.title}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {history.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEvolution(!showEvolution)}
                className={`text-xs ${showEvolution ? 'text-amber-400' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
              >
                <History size={14} className="mr-1" />
                Evolution ({history.length})
              </Button>
            )}
            {missingCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFillMissing}
                className="text-amber-400 hover:text-amber-300 text-xs"
              >
                <Zap size={14} className="mr-1" />
                Fill {missingCount} Missing
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRunAnalysis}
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)] text-xs"
            >
              <RefreshCw size={14} className="mr-1" />
              Re-analyze
            </Button>
          </div>
        </div>

        {/* Script Evolution (toggleable) */}
        {showEvolution && history.length > 1 && (
          <div className="mb-6 px-6 lg:px-8">
            <ScriptEvolution history={history} currentAnalysis={analysis} />
          </div>
        )}

        <GreenlightDashboardV2 analysis={analysis} />
      </div>
    </div>
  );
}
